import {
  rpsBeneficiaryCatogories,
  isRPSOCRTraditionalAccount,
  rpsAccountTypes,
  beneFilterByType,
  rpsBeneficiaryTypes
} from '../shared/utils';
import { formatDate } from '../../shared/dateUtils';
import {
  investmentMethods,
  accountTypes,
  rpsToAccountTypes,
  rpsToAccountTypesForDisplay
} from '../../shared/utils';
import { withDefault, getQueryVariable } from '../../../core/util';

export const beneErrorText = `There are more than 4 primary or secondary beneficiaries. OCR cannot be 
used to initiate the rollover process. Please use DocuSign to set up the rollover 
IRA and update the beneficiaries as needed.`;

const filterByCategory = (beneficiaries, category): Array<any> => {
  return (
    beneficiaries && beneficiaries.filter(x => x.beneCategoryType === category)
  );
};

export const formatBeneficiariesForApp = (
  primaryBeneficiaries,
  secondaryBeneficiaries
): any => {
  const primaryBenes = JSON.parse(JSON.stringify(primaryBeneficiaries));
  const secondaryBenes = JSON.parse(JSON.stringify(secondaryBeneficiaries));

  const primaryBeneIndividuals = filterByCategory(
    primaryBenes,
    rpsBeneficiaryCatogories.INDIVIDUAL
  );
  const secondaryBeneIndividuals = filterByCategory(
    secondaryBenes,
    rpsBeneficiaryCatogories.INDIVIDUAL
  );
  const primaryBeneEntities = filterByCategory(
    primaryBenes,
    rpsBeneficiaryCatogories.ENTITY
  );
  const secondaryBeneEntities = filterByCategory(
    secondaryBenes,
    rpsBeneficiaryCatogories.ENTITY
  );

  const individuals = [...primaryBeneIndividuals, ...secondaryBeneIndividuals];
  const entities = [...primaryBeneEntities, ...secondaryBeneEntities];

  /* eslint-disable no-param-reassign */
  entities.forEach(bene => {
    bene.name = bene.entityName;
    bene.taxID = bene.taxID.replace(/[^*0-9]/g, '');
    bene.percentage = Number(bene.percentageDistribution);
    bene.beneficiaryType = bene.beneType;
    delete bene.dateOfBirth;
    delete bene.entityName;
    delete bene.category;
    delete bene.beneType;
    delete bene.beneCategoryType;
    delete bene.percentageDistribution;
  });
  individuals.forEach(bene => {
    bene.taxID = bene.taxID.replace(/[^*0-9]/g, '');
    bene.percentage = Number(bene.percentageDistribution);
    bene.beneficiaryType = bene.beneType;
    bene.relationship = bene?.relationship?.toUpperCase();
    delete bene.category;
    delete bene.beneType;
    delete bene.beneCategoryType;
    delete bene.percentageDistribution;
  });
  /* eslint-enable no-param-reassign */
  return {
    individuals,
    entities
  };
};

export const getOCRRolloverDetails = (store): any => {
  const { app, loadedData, mailingAddressDifferent } = store;
  const { retirementPlanInfo } = app;

  const todayDate = formatDate(new Date(), 'MM/DD/YYYY');
  const accounsArr = loadedData?.accounts;

  const funds = loadedData?.chosenFundsDetails?.map(
    ({ name, percent, trpCode }) => {
      const accountInfo = accounsArr?.find(
        acct => trpCode === acct?.investmentVehicle?.trpCode
      );
      return (
        accountInfo && {
          accountNumber: accountInfo?.accountNumber,
          fundName: name,
          amountPercent: percent
        }
      );
    }
  );

  return {
    funds,
    planNumber: retirementPlanInfo?.planKey?.planNumber,
    planName: retirementPlanInfo?.planName,
    rolloverAmount: Number(store?.amount),
    todayDate,
    sameAddress: Boolean(!mailingAddressDifferent)
  };
};

export const getExistingRPSToAccountType = (existingAgreementType): string => {
  const type = {
    [accountTypes.ROTH]: rpsToAccountTypes.ROTH,
    [accountTypes.TRADITIONAL]: rpsToAccountTypes.TRADITIONAL,
    [accountTypes.ROTH_ROLLOVER]: rpsToAccountTypes.ROTH_ROLLOVER,
    [accountTypes.ROLLOVER]: rpsToAccountTypes.ROLLOVER
  }[existingAgreementType];
  return type;
};

export const getRPSToAccountTypeForDisplay = (rpsToAccType): string => {
  const type = {
    [rpsToAccountTypes.ROTH]: rpsToAccountTypesForDisplay.ROTH,
    [rpsToAccountTypes.TRADITIONAL]: rpsToAccountTypesForDisplay.TRADITIONAL,
    [rpsToAccountTypes.ROTH_ROLLOVER]:
      rpsToAccountTypesForDisplay.ROTH_ROLLOVER,
    [rpsToAccountTypes.ROLLOVER]: rpsToAccountTypesForDisplay.ROLLOVER
  }[rpsToAccType];
  return type;
};

export const getDefaultRPSToAccountType = (isOCRTraditionalAcc): string => {
  return isOCRTraditionalAcc
    ? rpsToAccountTypes.ROLLOVER
    : rpsToAccountTypes.ROTH_ROLLOVER;
};

export const formatOCRPDFPayload = (store): any => {
  const { app, loadedData, accountType } = store;
  const {
    primaryBeneficiaries,
    secondaryBeneficiaries,
    hasExistingIRA,
    existingAgreementType
  } = loadedData;
  const rolloverDetails = getOCRRolloverDetails(store);

  const primaryBenes = primaryBeneficiaries?.map(
    ({
      name,
      entityName,
      taxID,
      dateOfBirth,
      relationship,
      percentageDistribution,
      beneCategoryType
    }) => {
      if (beneCategoryType === rpsBeneficiaryCatogories.INDIVIDUAL) {
        const { firstName, middleName, lastName } = name;

        const beneName = middleName
          ? `${firstName} ${middleName} ${lastName}`
          : `${firstName} ${lastName}`;

        return {
          name: beneName,
          taxID,
          relationship,
          percentage: Number(percentageDistribution),
          dateOfBirth
        };
      }
      return {
        name: entityName,
        taxID,
        percentage: Number(percentageDistribution),
        relationship: 'Entity',
        dateOfBirth: ''
      };
    }
  );

  const secondaryBenes = secondaryBeneficiaries?.map(
    ({
      name,
      entityName,
      taxID,
      dateOfBirth,
      relationship,
      percentageDistribution,
      beneCategoryType
    }) => {
      if (beneCategoryType === rpsBeneficiaryCatogories.INDIVIDUAL) {
        const { firstName, middleName, lastName } = name;

        const beneName = middleName
          ? `${firstName} ${middleName} ${lastName}`
          : `${firstName} ${lastName}`;

        return {
          name: beneName,
          taxID,
          relationship,
          percentage: Number(percentageDistribution),
          dateOfBirth
        };
      }
      return {
        name: entityName,
        taxID,
        percentage: Number(percentageDistribution),
        relationship: 'Entity',
        dateOfBirth: ''
      };
    }
  );

  const isOCRTraditionalAcc = isRPSOCRTraditionalAccount(accountType);

  return {
    customer: app?.customer,
    deliveryType: loadedData?.ocrDeliveryMethod,
    rolloverDetails,
    primaryBeneficiaries: primaryBenes,
    secondaryBeneficiaries: secondaryBenes,
    paperless: withDefault(store?.loadedData?.paperlessDelivery, false),
    toAccountType: hasExistingIRA
      ? getExistingRPSToAccountType(existingAgreementType)
      : getDefaultRPSToAccountType(isOCRTraditionalAcc)
  };
};

export const getTRACDistributionStatus = (inputs): boolean => {
  if (inputs?.distributionStatus === 'YES') {
    return true;
  }
  return false;
};

export const getTRACRolloverNoticeInput = (inputs): boolean => {
  if (inputs?.rolloverOptionsNotice === 'YES') {
    return true;
  }
  return false;
};

export const getPreAuthPaperlesWithDrawalInput = (inputs): boolean => {
  if (inputs?.paperlessWithdrawls === 'YES') {
    return true;
  }
  return false;
};

export const getTermedOrRetiredStatusInput = (inputs): boolean => {
  if (inputs?.participantStatus === 'YES') {
    return true;
  }
  return false;
};

export const getRMDAgeByYearEndInput = (inputs): boolean => {
  if (inputs?.participantAge === 'YES') {
    return true;
  }
  return false;
};

export const getHasBrokerageHoldingsInput = (inputs): boolean => {
  if (inputs?.brokerageAssets === 'YES') {
    return true;
  }
  return false;
};

export const getHasLoanBalanceInput = (inputs): boolean => {
  if (inputs?.participantLoanBalance === 'YES') {
    return true;
  }
  return false;
};

export const getTRACLoanBalanceCashDistributionInput = (inputs): boolean => {
  if (inputs?.participantCashDistribution === 'YES') {
    return true;
  }
  return false;
};

export const getExistingFundAgreementId = (
  selectedTicker,
  existingAgreements,
  updatedExistingAgreements
): string => {
  const existingFundAgreement = existingAgreements?.filter(
    ({ ticker }) => selectedTicker === ticker
  );

  const updatedExistingAgreement = updatedExistingAgreements?.map(
    ({ agreementKey: { agreementNumber } }) => agreementNumber
  );

  const {
    agreementKey: { agreementID }
  } =
    existingFundAgreement?.find(agreement => {
      return updatedExistingAgreement?.includes(
        agreement?.agreementKey?.agreementNumber
      );
    }) || {};
  const existingFundAgreementId = agreementID?.toString();
  return existingFundAgreementId;
};

export const getOCRInvestmentMethodType = (
  existingFundFlag,
  newFundsFlag
): string => {
  if (existingFundFlag && newFundsFlag) {
    return investmentMethods.MULTIPLE;
  }
  if (existingFundFlag && !newFundsFlag) {
    return investmentMethods.EXISTING;
  }
  return investmentMethods.BYOP;
};

export const getIndividualBeneData = (benes): any => {
  const individualBenes = benes?.individuals?.map(individual => ({
    beneCategoryType: rpsBeneficiaryCatogories.INDIVIDUAL,
    beneficiaryType: individual?.beneficiaryType,
    dateOfBirth: individual?.dateOfBirthString,
    name: {
      firstName: individual?.name?.firstName,
      middleName: individual?.name?.middleName,
      lastName: individual?.name?.lastName
    },
    percentageDistribution: individual?.percentage,
    relationship: individual?.relationship?.toLowerCase(),
    taxID: individual?.taxID
  }));

  return individualBenes;
};

export const getEntitiesBeneData = (benes): any => {
  const entitiesBenes = benes?.entities?.map(entity => ({
    beneCategoryType: rpsBeneficiaryCatogories.ENTITY,
    beneficiaryType: entity?.beneficiaryType,
    percentageDistribution: entity?.percentage,
    entityName: entity?.name,
    taxID: entity?.taxID
  }));

  return entitiesBenes;
};

export const benesPresent = (beneData): boolean =>
  beneData?.individuals?.length >= 1 || beneData?.entities?.length >= 1;

export const getBeneCount = (filteredBenes): number => filteredBenes?.length;

export const getOCRPageTitle = (accountType): string => {
  const isOCRTraditionalAcc = isRPSOCRTraditionalAccount(accountType);
  return `One-Call ${
    isOCRTraditionalAcc ? 'Traditional ' : 'Roth '
  }Rollover (RPS)`;
};

export const getOCRSpecialTextOne = (accountType): string => {
  const isOCRTraditionalAcc = isRPSOCRTraditionalAccount(accountType);
  const accountText = isOCRTraditionalAcc ? 'Traditional' : 'Roth';
  return `[Read if participant will be liquidating company stock and rolling proceeds into a
    ${accountText} or ${accountText} Rollover IRA]`;
};

export const getOCRSpecialTextTwo = (accountType): string => {
  const isOCRTraditionalAcc = isRPSOCRTraditionalAccount(accountType);
  return `[Read if participant will be rolling over ${
    isOCRTraditionalAcc
      ? 'after-tax basis to a Traditional or Traditional Rollover IRA'
      : 'tax deferred money to a Roth or Roth Rollover IRA'
  }]`;
};

export const getRadioTitle = (accountType): string => {
  const isOCRTraditionalAcc = isRPSOCRTraditionalAccount(accountType);
  if (isOCRTraditionalAcc) {
    return `As part of this process, you will be rolling over after-tax basis to a Traditional or Traditional Rollover IRA.
    Please confirm that you understand you may lose possible tax advantages of rolling over your after-tax basis to a
    Traditional or Traditional Rollover IRA.`;
  }
  return `As part of this process, you will be rolling over tax deferred money into a Roth or Roth Rollover IRA.
  Converting will result in a taxable event. RMDs are not eligible for rolling over. Roth conversions are
  subject to a 5-year aging rule, distributions that do not meet the 5-year rule may be subject to a 10% penalty.
  Do you understand the implications of converting your 401k assets to a Roth or Roth Rollover IRA?`;
};

export const getRPSOCRAgreementType = (accountType): any => {
  switch (accountType) {
    case rpsAccountTypes.OMNI:
    case rpsAccountTypes.TRAC:
      // US436561-for Traditional OCR pull ROLLOVER_IRA and TRADITIONAL_IRA agreements
      return [accountTypes.ROLLOVER, accountTypes.TRADITIONAL];
    case rpsAccountTypes.ROTH_ROLLOVER_OMNI:
    case rpsAccountTypes.ROTH_ROLLOVER_TRAC:
      // US436561-for Roth OCR pull ROTH_ROLLOVER_IRA and ROTH_IRA agreements
      return [accountTypes.ROTH_ROLLOVER, accountTypes.ROTH];
    default:
      return [];
  }
};

/**
 * US682541: [Compliance] OCR - Apply Existing Benes
 * default agreement type for OCR flows to pre-fetch existing benes
 */
export const defaultRPSOCRAccountTypeToAgreementType = (
  accountType: string | undefined
): string | undefined => {
  switch (accountType) {
    case rpsAccountTypes.OMNI:
    case rpsAccountTypes.TRAC:
      return accountTypes.ROLLOVER;
    case rpsAccountTypes.ROTH_ROLLOVER_OMNI:
    case rpsAccountTypes.ROTH_ROLLOVER_TRAC:
      return accountTypes.ROTH_ROLLOVER;
    default:
      return undefined;
  }
};

export const isExistingFundsChosen = (
  selectedFunds: Array<any>,
  agreementType: string | undefined
): boolean => {
  return selectedFunds?.length > 0 && Boolean(agreementType);
};

export const isPartialRolloverAmt = (store): boolean => {
  const maxRolloverAmount = store?.loadedData?.maxRolloverAmt;
  if (maxRolloverAmount === '0') {
    return true;
  }
  return store?.amount < maxRolloverAmount;
};

export const checkForOCRAltFlow = (qs): boolean => {
  // example -> "?bpId=123456&altOCRFlow=true"
  const inAltOCRFlow = qs && getQueryVariable(qs, 'altOCRFlow');
  return Boolean(inAltOCRFlow);
};

/* eslint-disable complexity */
export const formatOCRBeneData = (ocrBeneficaries): any => {
  let primaryBeneficiaries: any = [];
  let secondaryBeneficiaries: any = [];

  let error = '';

  const iiBeneDataPresent = benesPresent(
    ocrBeneficaries?.individualInvestorBeneficiaries
  );
  const rpsBeneDataPresent = benesPresent(
    ocrBeneficaries?.retirementBeneficiaries
  );

  if (iiBeneDataPresent) {
    const individualIIBeneficiaries = getIndividualBeneData(
      ocrBeneficaries?.individualInvestorBeneficiaries
    );
    const entitiesIIBenes = getEntitiesBeneData(
      ocrBeneficaries?.individualInvestorBeneficiaries
    );

    const primaryIndividuals = beneFilterByType(
      individualIIBeneficiaries,
      rpsBeneficiaryTypes.PRIMARY
    );
    const secondaryIndividuals = beneFilterByType(
      individualIIBeneficiaries,
      rpsBeneficiaryTypes.SECONDARY
    );
    const primaryIIEntities = beneFilterByType(
      entitiesIIBenes,
      rpsBeneficiaryTypes.PRIMARY
    );
    const secondaryIIEntities = beneFilterByType(
      entitiesIIBenes,
      rpsBeneficiaryTypes.SECONDARY
    );
    primaryBeneficiaries = [...primaryIndividuals, ...primaryIIEntities];
    secondaryBeneficiaries = [...secondaryIndividuals, ...secondaryIIEntities];

    const iiPrimaryBeneCount = getBeneCount(primaryBeneficiaries);
    const iiSecondaryBeneCount = getBeneCount(secondaryBeneficiaries);

    if (iiPrimaryBeneCount > 4 || iiSecondaryBeneCount > 4) {
      error = beneErrorText;
    }
  } else if (rpsBeneDataPresent) {
    const individualRPSBenes = getIndividualBeneData(
      ocrBeneficaries?.retirementBeneficiaries
    );
    const entitiesRPSBenes = getEntitiesBeneData(
      ocrBeneficaries?.retirementBeneficiaries
    );

    const primaryRPSIndividuals = beneFilterByType(
      individualRPSBenes,
      rpsBeneficiaryTypes.PRIMARY
    );
    const secondaryRPSIndividuals = beneFilterByType(
      individualRPSBenes,
      rpsBeneficiaryTypes.SECONDARY
    );
    const primaryRPSEntities = beneFilterByType(
      entitiesRPSBenes,
      rpsBeneficiaryTypes.PRIMARY
    );
    const secondaryRPSEntities = beneFilterByType(
      entitiesRPSBenes,
      rpsBeneficiaryTypes.SECONDARY
    );
    primaryBeneficiaries = [...primaryRPSIndividuals, ...primaryRPSEntities];
    secondaryBeneficiaries = [
      ...secondaryRPSIndividuals,
      ...secondaryRPSEntities
    ];

    const rpsPrimaryBeneCount = getBeneCount(primaryBeneficiaries);
    const rpsSecondaryBeneCount = getBeneCount(secondaryBeneficiaries);

    if (rpsPrimaryBeneCount > 4 || rpsSecondaryBeneCount > 4) {
      error = beneErrorText;
    }
  }

  return {
    error,
    primaryBeneficiaries,
    secondaryBeneficiaries,
    iiBeneDataPresent,
    rpsBeneDataPresent
  };
};
/* eslint-enable complexity */
